import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { MoreMenu, type MoreItem } from "./more-menu";

export type BulkAction = {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** The main bulk action (filled). At most one. */
  primary?: boolean;
};

/** How many labelled actions show on the bar; the rest go into "More". */
export const MAX_BULK_ACTIONS = 4;

// how many bulk bars are on screen: the toaster lifts phone toasts above it
let visibleBars = 0;
const ls = new Set<() => void>();
const bump = (d: number) => {
  visibleBars += d;
  ls.forEach((l) => l());
};
export const useBulkBarVisible = () =>
  useSyncExternalStore(
    (l) => {
      ls.add(l);
      return () => ls.delete(l);
    },
    () => visibleBars > 0,
    () => false,
  );

/**
 * Appears only while something is selected: "{n} selected", up to 4 labelled actions, "More" for the rest, and
 * "Clear selection". Floats at the bottom centre, above the phone tab bar, and never covers the toasts.
 * Esc clears the selection.
 */
export function BulkBar({ count, actions, onClear, label, className }: { count: number; actions: BulkAction[]; onClear: () => void; label?: ReactNode; className?: string }) {
  const { t } = useT();
  const open = count > 0;
  useEffect(() => {
    if (!open) return;
    bump(1);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented && !document.querySelector("[role=dialog],[role=menu]")) onClear();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      bump(-1);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClear]);
  const shown = actions.slice(0, MAX_BULK_ACTIONS);
  const rest: MoreItem[] = actions.slice(MAX_BULK_ACTIONS).map((a) => ({ id: a.id, label: a.label, icon: a.icon, onSelect: a.onClick, danger: a.danger, disabled: a.disabled }));
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="region"
          aria-label={t("kit.bulkActions")}
          data-testid="bulk-bar"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className={cn(
            "fixed inset-x-0 bottom-[calc(76px+env(safe-area-inset-bottom))] z-40 mx-auto flex w-max max-w-[calc(100vw-1.5rem)] flex-wrap items-center gap-1.5 rounded-2xl border border-line bg-surface p-1.5 shadow-pop lg:bottom-6",
            className,
          )}
        >
          <span className="num px-2.5 text-[13px] font-semibold text-ink" aria-live="polite">
            {label ?? t("kit.selected", { count })}
          </span>
          <span className="h-5 w-px bg-line" aria-hidden />
          {shown.map((a) => (
            <Button key={a.id} size="sm" variant={a.primary ? "primary" : a.danger ? "danger" : "ghost"} icon={a.icon} onClick={a.onClick} disabled={a.disabled} loading={a.loading} data-testid={`bulk-${a.id}`}>
              {a.label}
            </Button>
          ))}
          {rest.length > 0 && <MoreMenu items={rest} variant="ghost" size="sm" testId="bulk-more" />}
          <Button size="icon-sm" variant="ghost" onClick={onClear} aria-label={t("kit.clearSelection")} title={t("kit.clearSelection")} data-testid="bulk-clear">
            <X />
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
